import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Separator } from '../../ui/separator';
import {
  Upload,
  FileText,
  MoreVertical,
  Eye,
  Edit,
  CheckCircle,
  X,
  User,
  Clock,
  Download,
  Paperclip,
  Trash2,
  AlertTriangle,
  Save,
  Search
} from 'lucide-react';
import { projectService } from '../../../src/services/project.service';
import { authService } from '../../../src/services/auth.service';
import { userService } from '../../../src/services/user.service';
import { submittalService } from '../../../src/services/submittal.service';
import { fileStorageService } from '../../../src/services/file-storage.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';

// New Submittal Modal Component
function NewSubmittalModal({ onSubmittalCreated }: { onSubmittalCreated?: () => void }) {
  const [formData, setFormData] = useState({
    title: '',
    project: '',
    category: '',
    priority: 'medium',
    description: '',
    submittedBy: 'Current User',
    assignedTo: '',
    dueDate: '',
    specSection: '',
    drawingReference: '',
    submittalType: '',
    revisionNumber: '01',
    contractReference: '',
    notes: '',
    attachments: [] as Array<{ file_id: string; file_path: string }>
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; size: number; type: string; filePath: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [open, setOpen] = useState(false);

  // Load projects and users when modal opens
  useEffect(() => {
    if (open) {
      loadProjects();
      loadUsers();
    }
  }, [open]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      console.log('Submittal Modal - Projects API Response:', response);
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Submittal Modal - Setting projects:', projectData);
        setProjects(projectData);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      console.log('Submittal Modal - Users API Response:', response);
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Submittal Modal - Setting users:', userData);
        setUsers(userData);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const { documentService } = await import('../../../src/services/document.service');
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      const uploadPromises = Array.from(files).map(async (file) => {
        // Upload file to MinIO
        const uploadResponse = await documentService.uploadFile(file, orgId);

        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return {
          id: uploadResponse.data.storage_id,
          name: file.name,
          size: file.size,
          type: file.type,
          filePath: uploadResponse.data.storage_id
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      setUploadedFiles([...uploadedFiles, ...newFiles]);

      // Update attachments in form data with file objects
      const attachmentObjects = [...formData.attachments, ...newFiles.map(f => ({ file_id: f.id, file_path: f.filePath }))];
      setFormData({ ...formData, attachments: attachmentObjects });

      showSuccess({
        title: 'Files Uploaded',
        message: `${newFiles.length} file(s) uploaded successfully`
      });

    } catch (error: any) {
      console.error('Error uploading files:', error);
      showError({
        title: 'Upload Failed',
        message: error.message || 'Failed to upload files. Please try again.'
      });
    } finally {
      setIsUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
    setFormData({
      ...formData,
      attachments: formData.attachments.filter(att => att.file_id !== fileId)
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Submittal title is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category selection is required';
    }
    if (!formData.submittalType) {
      newErrors.submittalType = 'Submittal type is required';
    }
    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Assignment is required';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;
      const userId = userProfile?.id;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      if (!userId) {
        throw new Error('User ID not found');
      }

      // Get next submittal number
      const submittalNumberResponse = await submittalService.getNextSubmittalNumber(formData.project);
      if (!submittalNumberResponse.success || !submittalNumberResponse.data) {
        throw new Error('Failed to generate submittal number');
      }

      // Prepare submittal payload with datetime format
      const submittalPayload = {
        organization_id: orgId,
        project_id: formData.project,
        submittal_number: submittalNumberResponse.data.submittal_number,
        submitted_by: userId,
        submit_date: new Date().toISOString(),
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        submittal_type: formData.submittalType,
        priority: formData.priority as 'low' | 'medium' | 'high' | 'urgent',
        assigned_to: formData.assignedTo,
        due_date: formData.dueDate ? `${formData.dueDate}T17:00:00` : undefined,
        spec_section: formData.specSection || undefined,
        drawing_reference: formData.drawingReference || undefined,
        contract_reference: formData.contractReference || undefined,
        revision_number: formData.revisionNumber || undefined,
        notes: formData.notes || undefined,
        attachments: formData.attachments.length > 0 ? formData.attachments.map(a => a.file_path) : undefined
      };

      const response = await submittalService.createSubmittal(submittalPayload);

      if (response.success) {
        if (formData.assignedTo) {
          notificationService.submittalCreated(
            formData.assignedTo,
            submittalNumberResponse.data.submittal_number,
            formData.project,
          );
        }
        showSuccess({
          title: 'Submittal Created',
          message: `Submittal ${submittalNumberResponse.data.submittal_number} created successfully!`
        });

        // Reset form
        setFormData({
          title: '',
          project: '',
          category: '',
          priority: 'medium',
          description: '',
          submittedBy: 'Current User',
          assignedTo: '',
          dueDate: '',
          specSection: '',
          drawingReference: '',
          submittalType: '',
          revisionNumber: '01',
          contractReference: '',
          notes: '',
          attachments: []
        });
        setUploadedFiles([]);
        setErrors({});
        setOpen(false);
        if (onSubmittalCreated) onSubmittalCreated();
      } else {
        showError({
          title: 'Failed to Create Submittal',
          message: response.message || 'An error occurred. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Error creating submittal:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'An error occurred. Please try again.';
      showError({
        title: 'Failed to Create Submittal',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Upload className="h-4 w-4 mr-2" />
          New Submittal
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Create New Submittal
          </DialogTitle>
          <DialogDescription>
            Submit documents for review and approval
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Submittal Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="submittal-title">Submittal Title *</Label>
              <Input
                id="submittal-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Structural Steel Shop Drawings - Phase 1"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="submittal-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name || project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="submittal-category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shop-drawings">Shop Drawings</SelectItem>
                    <SelectItem value="product-data">Product Data</SelectItem>
                    <SelectItem value="samples">Samples</SelectItem>
                    <SelectItem value="mix-designs">Mix Designs</SelectItem>
                    <SelectItem value="test-reports">Test Reports</SelectItem>
                    <SelectItem value="calculations">Calculations</SelectItem>
                    <SelectItem value="warranties">Warranties</SelectItem>
                    <SelectItem value="operation-manuals">Operation Manuals</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="submittal-type">Submittal Type *</Label>
                <Select value={formData.submittalType} onValueChange={(value) => setFormData({...formData, submittalType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="for-approval">For Approval</SelectItem>
                    <SelectItem value="for-review">For Review</SelectItem>
                    <SelectItem value="for-information">For Information</SelectItem>
                    <SelectItem value="resubmittal">Resubmittal</SelectItem>
                    <SelectItem value="final-submittal">Final Submittal</SelectItem>
                  </SelectContent>
                </Select>
                {errors.submittalType && <p className="text-sm text-destructive">{errors.submittalType}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="revision-number">Revision Number</Label>
                <Input
                  id="revision-number"
                  value={formData.revisionNumber}
                  onChange={(e) => setFormData({...formData, revisionNumber: e.target.value})}
                  placeholder="01"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="submittal-description">Description</Label>
              <Textarea
                id="submittal-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Detailed description of the submittal contents..."
                rows={3}
              />
            </div>
          </div>

          {/* Assignment & Schedule */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Assignment & Schedule</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assigned-to">Assigned To *</Label>
                <Select value={formData.assignedTo} onValueChange={(value) => setFormData({...formData, assignedTo: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reviewer" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => {
                      const displayName = user.full_name ||
                        (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
                      const displayText = user.job_title ? `${displayName} - ${user.job_title}` : displayName;
                      return (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {displayText}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {errors.assignedTo && <p className="text-sm text-destructive">{errors.assignedTo}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="submittal-priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="submittal-due-date">Review Due Date *</Label>
              <Input
                id="submittal-due-date"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
              />
              {errors.dueDate && <p className="text-sm text-destructive">{errors.dueDate}</p>}
            </div>
          </div>

          {/* Reference Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Reference Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="spec-section">Specification Section</Label>
                <Input
                  id="spec-section"
                  value={formData.specSection}
                  onChange={(e) => setFormData({...formData, specSection: e.target.value})}
                  placeholder="e.g., 05 12 00 - Structural Steel"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drawing-ref">Drawing Reference</Label>
                <Input
                  id="drawing-ref"
                  value={formData.drawingReference}
                  onChange={(e) => setFormData({...formData, drawingReference: e.target.value})}
                  placeholder="e.g., S-201, S-301"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contract-ref">Contract Reference</Label>
              <Input
                id="contract-ref"
                value={formData.contractReference}
                onChange={(e) => setFormData({...formData, contractReference: e.target.value})}
                placeholder="Contract section or clause reference"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="submittal-notes">Notes</Label>
              <Textarea
                id="submittal-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Special instructions or additional information..."
                rows={2}
              />
            </div>
          </div>

          {/* Attachments Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Attachments</h4>
            </div>

            <div className="space-y-2">
              <Label>Upload Documents</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload-input')?.click()}
              >
                <input
                  id="file-upload-input"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.png,.jpg,.jpeg"
                />
                <div className="flex flex-col items-center gap-2">
                  {isUploading ? (
                    <>
                      <Upload className="h-10 w-10 text-primary animate-pulse" />
                      <p className="text-sm font-medium text-primary">Uploading files...</p>
                      <p className="text-xs text-muted-foreground">Please wait</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-400" />
                      <p className="text-sm font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground">
                        PDF, DOC, DOCX, XLS, XLSX, DWG, PNG, JPG (Max 10MB per file)
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Uploaded Files ({uploadedFiles.length})</Label>
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(file.id)}
                        className="flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Creating...' : 'Create Submittal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Submittal Review Modal Component
function SubmittalReviewModal({ submittal, trigger, onReviewSubmitted, open: externalOpen, onOpenChange }: {
  submittal: any; trigger?: React.ReactNode; onReviewSubmitted?: () => void;
  open?: boolean; onOpenChange?: (v: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (v: boolean) => { setInternalOpen(v); onOpenChange?.(v); };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewData, setReviewData] = useState({
    status: submittal.status,
    comments: '',
    reviewDate: new Date().toISOString().split('T')[0],
    reviewedBy: 'Current User',
    requiresResubmission: false,
    approvalConditions: ''
  });

  const handleSubmitReview = async () => {
    try {
      setIsSubmitting(true);

      const reviewPayload = {
        status: reviewData.status as 'approved' | 'approved-as-noted' | 'revise-resubmit' | 'rejected' | 'acknowledged',
        review_comments: reviewData.comments,
        review_date: new Date().toISOString()
      };

      const response = await submittalService.reviewSubmittal(submittal.submittal_id, reviewPayload);

      if (response.success) {
        showSuccess({
          title: 'Review Submitted',
          message: 'Submittal review submitted successfully'
        });
        setIsOpen(false);

        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } else {
        showError({
          title: 'Error',
          message: 'Failed to submit review'
        });
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      showError({
        title: 'Error',
        message: error.message || 'Failed to submit review'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild onClick={() => setIsOpen(true)}>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Review Submittal
          </DialogTitle>
          <DialogDescription>
            Review and provide feedback for: {submittal.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Submittal Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Category:</span>
                <p className="text-muted-foreground">{submittal.category}</p>
              </div>
              <div>
                <span className="font-medium">Type:</span>
                <p className="text-muted-foreground">{submittal.submittal_type}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-status">Review Status *</Label>
            <Select value={reviewData.status} onValueChange={(value) => setReviewData({...reviewData, status: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="approved-as-noted">Approved As Noted</SelectItem>
                <SelectItem value="revise-resubmit">Revise and Resubmit</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-comments">Review Comments</Label>
            <Textarea
              id="review-comments"
              value={reviewData.comments}
              onChange={(e) => setReviewData({...reviewData, comments: e.target.value})}
              placeholder="Provide detailed review comments..."
              rows={4}
            />
          </div>

          {(reviewData.status === 'approved-as-noted' || reviewData.status === 'approved') && (
            <div className="space-y-2">
              <Label htmlFor="approval-conditions">Approval Conditions (if any)</Label>
              <Textarea
                id="approval-conditions"
                value={reviewData.approvalConditions}
                onChange={(e) => setReviewData({...reviewData, approvalConditions: e.target.value})}
                placeholder="Any conditions or requirements for approval..."
                rows={2}
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requires-resubmission"
              checked={reviewData.requiresResubmission}
              onChange={(e) => setReviewData({...reviewData, requiresResubmission: e.target.checked})}
              className="rounded border-border"
            />
            <Label htmlFor="requires-resubmission">Requires Resubmission</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmitReview} disabled={isSubmitting}>
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Submittal View Details Modal Component
function SubmittalViewDetailsModal({ submittal, trigger, projects, users, open: externalOpen, onOpenChange }: {
  submittal: any; trigger?: React.ReactNode; projects: any[]; users: any[];
  open?: boolean; onOpenChange?: (v: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (v: boolean) => { setInternalOpen(v); onOpenChange?.(v); };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || project?.project_name || 'Unknown Project';
  };

  const getUserName = (userId: string | undefined | null) => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.last_name || user.full_name || user.name || user.email || userId;
  };

  const formatCategory = (category: string) => {
    if (!category) return 'N/A';
    return category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatSubmittalType = (type: string) => {
    if (!type) return 'N/A';
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild onClick={() => setIsOpen(true)}>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <FileText className="h-5 w-5" />
            Submittal Details - {submittal.submittal_number}
          </DialogTitle>
          <DialogDescription>Complete information for this submittal</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title and Status */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{submittal.title}</h3>
            <div className="flex items-center gap-2">
              <Badge variant={submittal.status === 'approved' ? 'default' : submittal.status === 'pending-review' ? 'secondary' : 'outline'}>
                {submittal.status}
              </Badge>
              <Badge variant={submittal.priority === 'urgent' ? 'destructive' : 'outline'}>
                {submittal.priority} Priority
              </Badge>
              {submittal.revision_number && (
                <Badge variant="outline">Rev {submittal.revision_number}</Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Description */}
          {submittal.description && (
            <>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Description</Label>
                <p className="text-sm text-muted-foreground">{submittal.description}</p>
              </div>
              <Separator />
            </>
          )}

          {/* Submittal Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Project</Label>
              <p className="text-sm text-muted-foreground mt-1">{getProjectName(submittal.project_id)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Submittal Number</Label>
              <p className="text-sm text-muted-foreground mt-1 font-mono">{submittal.submittal_number}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Category</Label>
              <p className="text-sm text-muted-foreground mt-1">{formatCategory(submittal.category)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Type</Label>
              <p className="text-sm text-muted-foreground mt-1">{formatSubmittalType(submittal.submittal_type)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Submitted By</Label>
              <p className="text-sm text-muted-foreground mt-1">{getUserName(submittal.submitted_by)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Assigned To</Label>
              <p className="text-sm text-muted-foreground mt-1">{getUserName(submittal.assigned_to)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Submit Date</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {submittal.submit_date ? new Date(submittal.submit_date).toLocaleDateString() : new Date(submittal.created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Due Date</Label>
              <p className={`text-sm mt-1 ${submittal.due_date && new Date(submittal.due_date) < new Date() && submittal.status !== 'approved' ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                {submittal.due_date ? new Date(submittal.due_date).toLocaleDateString() : 'Not set'}
              </p>
            </div>
          </div>

          {/* References */}
          {(submittal.spec_section || submittal.drawing_reference || submittal.contract_reference) && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-semibold">References</Label>
                {submittal.spec_section && (
                  <div>
                    <span className="text-sm font-medium">Spec Section: </span>
                    <span className="text-sm text-muted-foreground">{submittal.spec_section}</span>
                  </div>
                )}
                {submittal.drawing_reference && (
                  <div>
                    <span className="text-sm font-medium">Drawing Reference: </span>
                    <span className="text-sm text-muted-foreground">{submittal.drawing_reference}</span>
                  </div>
                )}
                {submittal.contract_reference && (
                  <div>
                    <span className="text-sm font-medium">Contract Reference: </span>
                    <span className="text-sm text-muted-foreground">{submittal.contract_reference}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Notes */}
          {submittal.notes && (
            <>
              <Separator />
              <div>
                <Label className="text-sm font-semibold mb-2 block">Notes</Label>
                <p className="text-sm text-muted-foreground">{submittal.notes}</p>
              </div>
            </>
          )}

          {/* Review Information */}
          {submittal.review_comments && (
            <>
              <Separator />
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Review Comments</Label>
                  {submittal.review_date && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(submittal.review_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{submittal.review_comments}</p>
                {submittal.reviewed_by && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Reviewed by: {getUserName(submittal.reviewed_by)}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Attachments */}
          {submittal.attachments && submittal.attachments.length > 0 && (
            <>
              <Separator />
              <div>
                <Label className="text-sm font-semibold mb-2 block">Attachments ({submittal.attachments.length})</Label>
                <div className="space-y-2">
                  {submittal.attachments.map((fileId: string, index: number) => (
                    <div key={fileId} className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="text-sm">Attachment {index + 1}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await fileStorageService.previewFile(fileId);
                              if (res.success && res.data) {
                                const url = URL.createObjectURL(res.data);
                                window.open(url, '_blank');
                                setTimeout(() => URL.revokeObjectURL(url), 1000);
                              }
                            } catch (error: any) {
                              showError({ title: 'Preview failed', message: error.message || 'Failed to preview file' });
                            }
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await fileStorageService.downloadFile(fileId);
                              if (res.success && res.data) {
                                const url = URL.createObjectURL(res.data);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `attachment-${index + 1}`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(url), 1000);
                              }
                            } catch (error: any) {
                              showError({ title: 'Download failed', message: error.message || 'Failed to download file' });
                            }
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Submittal Edit Modal Component
function SubmittalEditModal({ submittal, trigger, projects, users, onSubmittalUpdated }: {
  submittal: any;
  trigger: React.ReactNode;
  projects: any[];
  users: any[];
  onSubmittalUpdated: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<string[]>(submittal.attachments || []);
  const [newUploadedFiles, setNewUploadedFiles] = useState<Array<{ id: string; name: string; size: number; type: string }>>([]);
  const [formData, setFormData] = useState({
    title: submittal.title || '',
    project_id: submittal.project_id || '',
    category: submittal.category || '',
    submittal_type: submittal.submittal_type || '',
    priority: submittal.priority || 'medium',
    assigned_to: submittal.assigned_to || '',
    due_date: submittal.due_date ? submittal.due_date.split('T')[0] : '',
    spec_section: submittal.spec_section || '',
    drawing_reference: submittal.drawing_reference || '',
    contract_reference: submittal.contract_reference || '',
    revision_number: submittal.revision_number || '',
    notes: submittal.notes || '',
    description: submittal.description || ''
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const { documentService } = await import('../../../src/services/document.service');
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      const uploadPromises = Array.from(files).map(async (file) => {
        const uploadResponse = await documentService.uploadFile(file, orgId);

        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return {
          id: uploadResponse.data.storage_id,
          name: file.name,
          size: file.size,
          type: file.type
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      setNewUploadedFiles([...newUploadedFiles, ...newFiles]);

      showSuccess({
        title: 'Files Uploaded',
        message: `${newFiles.length} file(s) uploaded successfully`
      });

    } catch (error: any) {
      console.error('Error uploading files:', error);
      showError({
        title: 'Upload Failed',
        message: error.message || 'Failed to upload files. Please try again.'
      });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveNewFile = (fileId: string) => {
    setNewUploadedFiles(newUploadedFiles.filter(f => f.id !== fileId));
  };

  const handleRemoveExistingFile = (fileId: string) => {
    setExistingAttachments(existingAttachments.filter(id => id !== fileId));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Combine existing and new attachments
      const allAttachments = [...existingAttachments, ...newUploadedFiles.map(f => f.id)];

      const updatePayload: any = {
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        submittal_type: formData.submittal_type,
        priority: formData.priority as 'low' | 'medium' | 'high' | 'urgent',
        assigned_to: formData.assigned_to,
        due_date: formData.due_date ? `${formData.due_date}T17:00:00` : undefined,
        spec_section: formData.spec_section || undefined,
        drawing_reference: formData.drawing_reference || undefined,
        contract_reference: formData.contract_reference || undefined,
        revision_number: formData.revision_number || undefined,
        notes: formData.notes || undefined,
        attachments: allAttachments.length > 0 ? allAttachments : undefined
      };

      const response = await submittalService.updateSubmittal(submittal.submittal_id, updatePayload);

      if (response.success) {
        showSuccess({
          title: 'Submittal Updated',
          message: 'Submittal updated successfully!'
        });
        setIsOpen(false);
        onSubmittalUpdated();
      } else {
        showError({
          title: 'Failed to Update Submittal',
          message: response.message || 'An error occurred. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Error updating submittal:', error);
      showError({
        title: 'Failed to Update Submittal',
        message: error.message || 'An error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild onClick={() => setIsOpen(true)}>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Submittal
          </DialogTitle>
          <DialogDescription>
            Update submittal information for {submittal.submittal_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title *</Label>
            <Input
              id="edit-title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Submittal title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Detailed description..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-project">Project *</Label>
              <Select value={formData.project_id} onValueChange={(value) => setFormData({...formData, project_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name || project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-assigned-to">Assigned To *</Label>
              <Select value={formData.assigned_to} onValueChange={(value) => setFormData({...formData, assigned_to: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shop-drawings">Shop Drawings</SelectItem>
                  <SelectItem value="product-data">Product Data</SelectItem>
                  <SelectItem value="samples">Samples</SelectItem>
                  <SelectItem value="mix-designs">Mix Designs</SelectItem>
                  <SelectItem value="test-reports">Test Reports</SelectItem>
                  <SelectItem value="certifications">Certifications</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Submittal Type *</Label>
              <Select value={formData.submittal_type} onValueChange={(value) => setFormData({...formData, submittal_type: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="for-approval">For Approval</SelectItem>
                  <SelectItem value="for-review">For Review</SelectItem>
                  <SelectItem value="for-information">For Information</SelectItem>
                  <SelectItem value="for-record">For Record</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-due-date">Due Date *</Label>
              <Input
                id="edit-due-date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-spec-section">Spec Section</Label>
              <Input
                id="edit-spec-section"
                value={formData.spec_section}
                onChange={(e) => setFormData({...formData, spec_section: e.target.value})}
                placeholder="e.g., 03 30 00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-revision">Revision Number</Label>
              <Input
                id="edit-revision"
                value={formData.revision_number}
                onChange={(e) => setFormData({...formData, revision_number: e.target.value})}
                placeholder="e.g., 01"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-drawing-ref">Drawing Reference</Label>
            <Input
              id="edit-drawing-ref"
              value={formData.drawing_reference}
              onChange={(e) => setFormData({...formData, drawing_reference: e.target.value})}
              placeholder="e.g., S-201, S-301"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-contract-ref">Contract Reference</Label>
            <Input
              id="edit-contract-ref"
              value={formData.contract_reference}
              onChange={(e) => setFormData({...formData, contract_reference: e.target.value})}
              placeholder="Contract reference number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          {/* Existing Attachments */}
          {existingAttachments.length > 0 && (
            <div className="space-y-2">
              <Label>Existing Attachments ({existingAttachments.length})</Label>
              <div className="border rounded-md divide-y max-h-32 overflow-y-auto">
                {existingAttachments.map((fileId: string, index: number) => (
                  <div key={fileId} className="flex items-center justify-between p-2 hover:bg-gray-50">
                    <div className="flex items-center gap-2 flex-1">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm">Attachment {index + 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const { documentService } = await import('../../../src/services/document.service');
                            const response = await documentService.downloadDocument(fileId);
                            if (response.success && response.data) {
                              const url = URL.createObjectURL(response.data);
                              window.open(url, '_blank');
                            }
                          } catch (error) {
                            console.error('Error previewing file:', error);
                          }
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveExistingFile(fileId)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New File Upload */}
          <div className="space-y-2">
            <Label>Upload New Attachments</Label>
            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => document.getElementById('edit-file-upload-input')?.click()}
            >
              <input
                id="edit-file-upload-input"
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={isUploading}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.png,.jpg,.jpeg"
              />
              <div className="flex flex-col items-center gap-2">
                {isUploading ? (
                  <>
                    <Upload className="h-8 w-8 text-primary animate-pulse" />
                    <p className="text-sm font-medium text-primary">Uploading files...</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400" />
                    <p className="text-sm font-medium">Click to upload files</p>
                    <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, XLS, DWG, PNG, JPG</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Newly Uploaded Files List */}
          {newUploadedFiles.length > 0 && (
            <div className="space-y-2">
              <Label>New Files ({newUploadedFiles.length})</Label>
              <div className="border rounded-md divide-y max-h-32 overflow-y-auto">
                {newUploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 hover:bg-gray-50">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveNewFile(file.id)}
                      className="flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting || isUploading}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Submittal Actions Menu Component
function SubmittalActionsMenu({ submittal, projects, users, onUpdate }: {
  submittal: any;
  projects: any[];
  users: any[];
  onUpdate: () => void;
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const handleDownload = async () => {
    const attachments: string[] = submittal.attachments ?? [];
    if (attachments.length === 0) {
      showError({ title: 'No attachments', message: 'This submittal has no attached files.' });
      return;
    }
    try {
      for (let i = 0; i < attachments.length; i++) {
        const res = await fileStorageService.downloadFile(attachments[i]);
        if (res.success && res.data) {
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${submittal.submittal_number}-attachment-${i + 1}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      }
      showSuccess({ title: 'Download complete', message: `${attachments.length} file(s) downloaded.` });
    } catch (error: any) {
      showError({ title: 'Download Failed', message: error.message || 'Failed to download submittal documents.' });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await submittalService.deleteSubmittal(submittal.submittal_id);

      if (response.success) {
        showSuccess({
          title: 'Submittal Withdrawn',
          message: 'Submittal has been withdrawn successfully.'
        });
        onUpdate();
      } else {
        showError({
          title: 'Failed to Withdraw',
          message: response.message || 'An error occurred. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Error deleting submittal:', error);
      showError({
        title: 'Failed to Withdraw',
        message: error.message || 'An error occurred. Please try again.'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <SubmittalViewDetailsModal
        submittal={submittal}
        projects={projects}
        users={users}
        open={viewOpen}
        onOpenChange={setViewOpen}
      />
      <SubmittalReviewModal
        submittal={submittal}
        onReviewSubmitted={onUpdate}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => setViewOpen(true)}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setReviewOpen(true)}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Review
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </DropdownMenuItem>
          <SubmittalEditModal
            submittal={submittal}
            projects={projects}
            users={users}
            onSubmittalUpdated={onUpdate}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            }
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                className="text-destructive"
                onSelect={(e) => e.preventDefault()}
                disabled={isDeleting}
              >
                <X className="h-4 w-4 mr-2" />
                {isDeleting ? 'Withdrawing...' : 'Withdraw'}
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Withdraw Submittal</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to withdraw submittal <span className="font-mono font-semibold">{submittal.submittal_number}</span>?
                  This action cannot be undone and will permanently remove this submittal from the system.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Withdrawing...' : 'Withdraw Submittal'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function Submittals() {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [submittals, setSubmittals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load submittals from database
  useEffect(() => {
    loadSubmittals();
    loadProjects();
    loadUsers();
  }, []);

  const loadSubmittals = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (projectFilter !== 'all') filters.project_id = projectFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (searchQuery) filters.search = searchQuery;

      console.log('Loading Submittals with filters:', filters);
      const response = await submittalService.getSubmittals(1, 100, filters);
      console.log('Submittal API Response:', response);

      if (response.success && response.data) {
        const submittalData = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);

        console.log('Setting Submittals:', submittalData);
        setSubmittals(submittalData);
      } else {
        const errorMsg = response.message || 'Failed to load submittals';
        console.error('API returned error:', errorMsg, response);
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Error loading submittals:', err);
      setError(err.message || 'Failed to load submittals');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      console.log('Submittals - Projects API Response:', response);
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);
        console.log('Submittals - Setting projects:', projectData);
        setProjects(projectData);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      console.log('Submittals - Users API Response:', response);
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Submittals - Setting users:', userData);
        setUsers(userData);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  // Reload when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadSubmittals();
    }, 300); // Debounce
    return () => clearTimeout(timer);
  }, [projectFilter, statusFilter, categoryFilter, searchQuery]);

  // Helper function to check if submittal is overdue
  const isOverdue = (submittal: any) => {
    if (!submittal.due_date || submittal.status === 'approved' || submittal.status === 'approved-as-noted' || submittal.status === 'acknowledged') return false;
    return new Date(submittal.due_date) < new Date();
  };

  // Map database project_id to project name
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || project?.project_name || 'Unknown Project';
  };

  // Map user_id to user name
  const getUserName = (userId: string | undefined | null) => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;

    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.last_name || user.full_name || user.name || user.email || userId;
  };

  // Format category for display
  const formatCategory = (category: string) => {
    if (!category) return 'N/A';
    return category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format submittal type for display
  const formatSubmittalType = (type: string) => {
    if (!type) return 'N/A';
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredSubmittals = submittals;

  const getStatusBadge = (status: string, overdue?: boolean) => {
    if (overdue) return <Badge variant="destructive">Overdue</Badge>;
    switch (status) {
      case "pending-review": return <Badge variant="secondary">Pending Review</Badge>;
      case "approved": return <Badge variant="default" className="bg-green-600">Approved</Badge>;
      case "approved-as-noted": return <Badge variant="default" className="bg-blue-600">Approved w/ Comments</Badge>;
      case "approved-with-comments": return <Badge variant="default" className="bg-blue-600">Approved w/ Comments</Badge>;
      case "revise-resubmit": return <Badge variant="default" className="bg-orange-600">Revise & Resubmit</Badge>;
      case "rejected": return <Badge variant="destructive">Rejected</Badge>;
      case "on-hold": return <Badge variant="outline">On Hold</Badge>;
      case "acknowledged": return <Badge variant="outline">Acknowledged</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent": return <Badge variant="destructive">Urgent</Badge>;
      case "high": return <Badge className="bg-orange-600">High</Badge>;
      case "medium": return <Badge variant="secondary">Medium</Badge>;
      case "low": return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{priority}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Submittals</h2>
          <p className="text-muted-foreground">Track document submittals and approvals</p>
        </div>
        <NewSubmittalModal onSubmittalCreated={loadSubmittals} />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Total Submittals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{submittals.length}</div>
            <p className="text-xs text-blue-600 mt-1">All projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Pending Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{submittals.filter(s => s.status === 'pending-review').length}</div>
            <p className="text-xs text-orange-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{submittals.filter(s => s.status === 'approved' || s.status === 'approved-as-noted' || s.status === 'approved-with-comments').length}</div>
            <p className="text-xs text-green-600 mt-1">Ready to proceed</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{submittals.filter(s => isOverdue(s)).length}</div>
            <p className="text-xs text-red-600 mt-1">Past due date</p>
          </CardContent>
        </Card>
      </div>

      {/* Submittal Management */}
      <Card>
        <CardHeader>
          <CardTitle>Submittal Register</CardTitle>
          <CardDescription>Manage document submittals and review process</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search submittals..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.name || project.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending-review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="approved-as-noted">Approved w/ Comments</SelectItem>
                <SelectItem value="revise-resubmit">Revise & Resubmit</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="shop-drawings">Shop Drawings</SelectItem>
                <SelectItem value="product-data">Product Data</SelectItem>
                <SelectItem value="samples">Samples</SelectItem>
                <SelectItem value="mix-designs">Mix Designs</SelectItem>
                <SelectItem value="test-reports">Test Reports</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading submittals...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-12">
              <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-medium mb-2">Error Loading Submittals</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadSubmittals} variant="outline">
                Try Again
              </Button>
            </div>
          )}

          {/* Submittal List */}
          {!loading && !error && (
            <div className="space-y-4">
              {filteredSubmittals.map((submittal) => (
                <div key={submittal.submittal_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm font-medium">{submittal.submittal_number}</span>
                        <h4 className="font-medium">{submittal.title}</h4>
                        {getStatusBadge(submittal.status, isOverdue(submittal))}
                        {getPriorityBadge(submittal.priority)}
                        {submittal.revision_number && <Badge variant="outline">Rev {submittal.revision_number}</Badge>}
                      </div>

                      {submittal.description && (
                        <p className="text-sm text-muted-foreground mb-3">{submittal.description}</p>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Project:</span>
                          <p className="text-muted-foreground">{getProjectName(submittal.project_id)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Category:</span>
                          <p className="text-muted-foreground">{formatCategory(submittal.category)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Type:</span>
                          <p className="text-muted-foreground">{formatSubmittalType(submittal.submittal_type)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Due Date:</span>
                          <p className={`text-muted-foreground ${isOverdue(submittal) ? 'text-red-600 font-medium' : ''}`}>
                            {submittal.due_date ? new Date(submittal.due_date).toLocaleDateString() : 'Not set'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-2">
                        <div>
                          <span className="font-medium">Submitted By:</span>
                          <p className="text-muted-foreground">{getUserName(submittal.submitted_by)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Assigned To:</span>
                          <p className="text-muted-foreground">{getUserName(submittal.assigned_to)}</p>
                        </div>
                        {submittal.spec_section && (
                          <div>
                            <span className="font-medium">Spec Section:</span>
                            <p className="text-muted-foreground">{submittal.spec_section}</p>
                          </div>
                        )}
                      </div>

                      {submittal.drawing_reference && (
                        <div className="mt-2">
                          <span className="font-medium text-sm">Drawing Reference: </span>
                          <span className="text-sm text-muted-foreground">{submittal.drawing_reference}</span>
                        </div>
                      )}

                      {submittal.review_comments && (
                        <div className="mt-3 p-2 bg-muted/30 rounded text-sm">
                          <span className="font-medium">Review Comments: </span>
                          <span className="text-muted-foreground">{submittal.review_comments}</span>
                        </div>
                      )}
                    </div>

                  <div className="flex items-center gap-2 ml-4">
                    <SubmittalViewDetailsModal
                      submittal={submittal}
                      projects={projects}
                      users={users}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      }
                    />
                    {submittal.status === 'pending-review' && (
                      <SubmittalReviewModal
                        submittal={submittal}
                        trigger={
                          <Button variant="default" size="sm">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        }
                      />
                    )}
                    <SubmittalActionsMenu submittal={submittal} projects={projects} users={users} onUpdate={loadSubmittals} />
                  </div>
                </div>
              </div>
              ))}

              {filteredSubmittals.length === 0 && (
                <div className="text-center py-12">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No submittals found</h3>
                  <p className="text-muted-foreground">
                    {searchQuery || projectFilter !== "all" || statusFilter !== "all" || categoryFilter !== "all"
                      ? "Try adjusting your search criteria or filters"
                      : "Create your first submittal to get started"
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { NewSubmittalModal, SubmittalReviewModal, SubmittalViewDetailsModal, SubmittalEditModal, SubmittalActionsMenu };
